import { Injectable, Injector, inject } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { LocationService } from '../platform/location.service';
import { SettingsService } from '../account/settings.service';
import { ConsoleLoggerService } from '../logging/console-logger.service';
import { MapService } from '../map/map.service';
import { RangeParams, Waypoint } from '../../models/range-params';
import { LatLng } from '../../models/latlng';
import { RouteListElement } from '../../models/route-list';
import { ECarSettings } from '../../models/ecar';
import { DisplayMode } from '../../models/display-mode';
import { WaypointAddress } from '../../models/route';
import { Position } from '../../models/position';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class WaypointService {

  // Single source of truth. Entries with wp >= 0 are real waypoints;
  // wp === -1 are empty UI placeholder rows (no coords).
  private routeList: RouteListElement[] = [];

  private selectedDisplayMode: DisplayMode = DisplayMode.RANGE;
  private searchedRangeOrRoute: boolean = false;
  private reverseGeocodedLocations: WaypointAddress[] | null = null;

  // Emits the coord-only view used by EvCalculationService.
  // Only fires when lat/lng/csid of a real waypoint changes — not on display_name updates.
  public readonly ObservableWaypointsParams = new BehaviorSubject<Waypoint[] | null>(null);
  // Emits on every routeList change (display names, placeholders, reorder…)
  public readonly ObservableRouteList = new BehaviorSubject<RouteListElement[] | null>(null);
  public readonly ObservableSelectedDisplayMode = new BehaviorSubject<DisplayMode | null>(null);
  public readonly paramsUpdate$ = new Subject<void>();
  public readonly ObservableReverseGeocodedLocations = new BehaviorSubject<WaypointAddress[] | null>(null);
  public readonly ObservableReverseGeocodingLoader = new BehaviorSubject<boolean | null>(null);
  public readonly ObservableSameCoordsError = new BehaviorSubject<boolean | null>(null);
  public readonly ObservableNoRoute = new BehaviorSubject<boolean>(false);

  private readonly locationService = inject(LocationService);
  private readonly settingsService = inject(SettingsService);
  private readonly consoleLoggerService = inject(ConsoleLoggerService);
  private readonly injector = inject(Injector);

  // Lazy to avoid circular DI: MapService → WaypointService → MapService
  private get mapService(): MapService {
    return this.injector.get(MapService);
  }

  // ===== Derived read helpers =====

  /** Real waypoints (lat/lng/csid) derived from routeList, excluding placeholder rows. */
  public getWaypointsParams(): Waypoint[] {
    return this.routeList
      .filter(el => el.wp != null && el.wp >= 0)
      .map(el => new Waypoint(el.lat, el.lng, el.csid));
  }

  public getStartCoordsParams(): Waypoint {
    const first = this.routeList.find(el => el.wp === 0) ?? this.routeList[0];
    return new Waypoint(first?.lat ?? 0, first?.lng ?? 0, first?.csid);
  }

  public getRouteList(): RouteListElement[] {
    return this.routeList;
  }

  // ===== Initialisation =====

  public restore(): void {
    const stored = localStorage.getItem('waypoints');
    if (stored && stored !== 'null') {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0 && 'display_name' in parsed[0]) {
          this.initFromRouteList(parsed as RouteListElement[]);
        } else if (Array.isArray(parsed) && parsed.length > 0) {
          this.initFromOldWaypoints(parsed as Waypoint[]);
        } else {
          this.initDefaultStart();
        }
      } catch (e) {
        this.initDefaultStart();
      }
    } else {
      this.initDefaultStart();
    }
    this.restoreDisplayMode();
  }

  /**
   * The mode the user was last in.
   *
   * Applied after the waypoints, because the init paths above infer ROUTE from having more than
   * one stop — an inference that is right for a list with no mode stored beside it, but wrong for
   * someone who planned a route and then went back to looking at their range. The stored value is
   * the deliberate one, so it wins.
   *
   * NAVIGATION is not restored on its own: guidance resumes from the "navigation" flag, and
   * landing in the navigation view without it would be a screen with nothing to guide along.
   */
  private restoreDisplayMode(): void {
    const stored = localStorage.getItem('displayMode');
    if (!stored) return;

    const known = Object.values(DisplayMode).find(mode => mode === stored);
    if (!known || known === DisplayMode.NAVIGATION) return;

    this.setSelectedDisplayModeInternal(known);
  }

  private initFromRouteList(list: RouteListElement[]): void {
    const valid = list.filter(el => el.lat !== 0 || el.lng !== 0);
    this.routeList = valid.map((el, i) => ({ ...el, wp: i }));
    this.emitAll();
    this.paramsUpdate$.next();
    if (this.routeList.length > 1) {
      this.setSelectedDisplayModeInternal(DisplayMode.ROUTE);
    }
  }

  private initFromOldWaypoints(waypoints: Waypoint[]): void {
    const valid = waypoints.filter(w => w.lat !== 0 || w.lng !== 0);
    this.routeList = valid.map((w, i) => ({ display_name: '', lat: w.lat, lng: w.lng, wp: i, csid: w.csid }));
    this.emitAll();
    this.paramsUpdate$.next();
    if (valid.length > 1) {
      this.setSelectedDisplayModeInternal(DisplayMode.ROUTE);
    }
    // One-time migration: backfill display names via reverse geocode
    valid.forEach((w, i) => {
      this.mapService.getReverseGeocode(w.lat, w.lng).subscribe((geocode) => {
        const entry = this.routeList[i];
        if (geocode && entry && entry.wp === i) {
          this.routeList[i] = { ...entry, display_name: geocode };
          this.ObservableRouteList.next([...this.routeList]);
        }
      });
    });
  }

  private initDefaultStart(): void {
    const defaultCoords = environment.env_name === 'dev'
      ? new LatLng(47.497913, 19.040236)
      : new LatLng(52.520008, 13.404954);
    const defaultName = environment.env_name === 'dev'
      ? 'Budapest, Hungary'
      : 'Karl-Liebknecht-Straße Berlin, Berlin, Germany';
    this.routeList = [{ display_name: defaultName, lat: defaultCoords.lat, lng: defaultCoords.lng, wp: 0 }];
    this.emitAll();
    this.paramsUpdate$.next();
  }

  // ===== Waypoints write API =====

  /**
   * Set waypoints from a Waypoint[] (coords only). Rebuilds routeList positionally,
   * preserving display_name where the entry at that position has the same coords.
   * Only emits ObservableWaypointsParams when coords actually changed.
   */
  public setWaypointParams(waypoints: Waypoint[]): void {
    if (waypoints.length > 1 && this.selectedDisplayMode === DisplayMode.RANGE) {
      this.setSelectedDisplayModeInternal(DisplayMode.ROUTE);
    }

    const filtered = waypoints.filter(w => w.lat !== 0 || w.lng !== 0);
    const current = this.getWaypointsParams();

    // Skip if nothing changed
    if (filtered.length === current.length) {
      const unchanged = filtered.every((w, i) =>
        w.lat === current[i].lat && w.lng === current[i].lng && w.csid === current[i].csid
      );
      if (unchanged) return;
    }

    // Rebuild routeList: preserve display_name where coords match existing entry at same position
    const newRouteList: RouteListElement[] = filtered.map((wp, i) => {
      const existing = this.routeList.find(el => el.wp === i);
      const sameCoords = existing && existing.lat === wp.lat && existing.lng === wp.lng;
      return {
        display_name: sameCoords ? existing!.display_name : '',
        lat: wp.lat,
        lng: wp.lng,
        wp: i,
        csid: wp.csid
      };
    });

    this.routeList = newRouteList;
    this.ObservableRouteList.next([...newRouteList]);
    this.ObservableWaypointsParams.next(filtered);
    this.ObservableNoRoute.next(filtered.length <= 1);
    this.paramsUpdate$.next();
  }

  public setStartCoordsParams(startcoords: Waypoint): void {
    const waypoints = this.getWaypointsParams();
    const copy = [...waypoints];
    copy[0] = startcoords;
    this.setWaypointParams(copy);
  }

  public unshiftStartCoordsParams(startcoords: Waypoint): void {
    const waypoints = this.getWaypointsParams();
    this.setWaypointParams([startcoords, ...waypoints]);
  }

  /** Silently update start coords from GPS — no recalc trigger, no display_name change. */
  public updateStartFromGps(position: Position): void {
    if (this.routeList.length > 0) {
      this.routeList[0] = { ...this.routeList[0], lat: position.Latitude, lng: position.Longitude };
      this.ObservableWaypointsParams.next(this.getWaypointsParams());
    }
  }

  public setStartToGeolocation(unshift?: boolean): LatLng | 'nogps' {
    const lastPosition = this.locationService.getLastPosition();
    if (lastPosition) {
      if (unshift) {
        this.unshiftStartCoordsParams(new LatLng(lastPosition.Latitude, lastPosition.Longitude));
      } else {
        this.setStartCoordsParams(new LatLng(lastPosition.Latitude, lastPosition.Longitude));
      }
      return new LatLng(lastPosition.Latitude, lastPosition.Longitude);
    }
    return 'nogps';
  }

  /** Add endpoint from search result or home/work shortcut. */
  public addEndpoint(endpoint: RouteListElement): void {
    if (this.getWaypointsParams().length > 1 && this.selectedDisplayMode === DisplayMode.RANGE) {
      this.deleteRouteListEntry(this.routeList.findIndex(el => el.wp === 1));
    }

    const cleanList = this.routeList.filter(el => el.lat !== 0 || el.lng !== 0);
    const nextWp = cleanList.length;
    cleanList.push({ display_name: endpoint.display_name, lat: endpoint.lat, lng: endpoint.lng, wp: nextWp, csid: endpoint.csid });
    this.routeList = cleanList;

    this.emitAll();
    if (cleanList.length > 1 && this.selectedDisplayMode === DisplayMode.RANGE) {
      this.setSelectedDisplayModeInternal(DisplayMode.ROUTE);
    }
    this.paramsUpdate$.next();
  }

  /** Add a charging station waypoint — uses its name as display_name, no reverse geocode needed. */
  public addChargingStationWaypoint(display_name: string, lat: number, lng: number, csid: string, index: number): void {
    if (this.getWaypointsParams().length > 1 && this.selectedDisplayMode === DisplayMode.RANGE) {
      this.deleteRouteListEntry(this.routeList.findIndex(el => el.wp === 1));
    }

    const cleanList = this.routeList.filter(el => el.lat !== 0 || el.lng !== 0);
    cleanList.splice(index, 0, { display_name, lat, lng, wp: index, csid });
    this.routeList = this.recalcWpIndices(cleanList);

    this.emitAll();
    if (this.routeList.length > 1 && this.selectedDisplayMode === DisplayMode.RANGE) {
      this.setSelectedDisplayModeInternal(DisplayMode.ROUTE);
    }
    this.paramsUpdate$.next();
  }

  /** Insert a waypoint at a given index with a geocoded name (e.g. from map popup). */
  public addRoutePoint(routeListElement: RouteListElement, index: number): void {
    if (this.getWaypointsParams().length > 1 && this.selectedDisplayMode === DisplayMode.RANGE) {
      this.deleteRouteListEntry(this.routeList.findIndex(el => el.wp === 1));
    }

    const cleanList = this.routeList.filter(el => el.lat !== 0 || el.lng !== 0);
    cleanList.splice(index, 0, {
      display_name: routeListElement.display_name,
      lat: routeListElement.lat,
      lng: routeListElement.lng,
      wp: index,
      csid: routeListElement.csid
    });
    this.routeList = this.recalcWpIndices(cleanList);

    this.emitAll();
    if (this.routeList.length > 1 && this.selectedDisplayMode === DisplayMode.RANGE) {
      this.setSelectedDisplayModeInternal(DisplayMode.ROUTE);
    }
    this.paramsUpdate$.next();
  }

  /** Update a specific slot from a search result, then rebuild waypoints. */
  public setWaypointAtIndex(index: number, element: Partial<RouteListElement>): void {
    this.routeList[index] = { ...this.routeList[index], ...element };
    this.rebuildFromRouteList();
  }

  /** Reorder and rebuild waypoints (after drag & drop). */
  public setRouteListAndRebuild(list: RouteListElement[]): void {
    this.routeList = list;
    this.ObservableRouteList.next([...list]);
    this.rebuildFromRouteList();
  }

  /** Display-only update — does NOT trigger recalculation. */
  public setRouteListDisplay(list: RouteListElement[]): void {
    this.routeList = list;
    this.ObservableRouteList.next([...list]);
  }

  /** Update only the start entry's display name (after reverse geocode). */
  public setStartDisplayName(display_name: string): void {
    if (this.routeList.length > 0) {
      this.routeList[0] = { ...this.routeList[0], display_name };
      this.ObservableRouteList.next([...this.routeList]);
    }
  }

  /** Add an empty placeholder row (no coords) to the UI list. */
  public addEmptyPlaceholder(): void {
    this.routeList = [...this.routeList, { display_name: '', lat: 0, lng: 0, wp: -1 }];
    this.ObservableRouteList.next([...this.routeList]);
  }

  public deleteWaypoint(waypointIdx: number): void {
    const routeListIdx = this.routeList.findIndex(el => el.wp === waypointIdx);
    if (routeListIdx !== -1) {
      this.routeList.splice(routeListIdx, 1);
      this.routeList = this.recalcWpIndices(this.routeList);
    }
    this.setSearchedRangeOrRoute(true);
    this.emitAll();
    this.paramsUpdate$.next();
  }

  /** Delete a routeList entry by display index; removes the waypoint if it has a valid wp. */
  public deleteRouteListEntry(routeListIndex: number): void {
    const entry = this.routeList[routeListIndex];
    if (!entry) return;

    this.routeList.splice(routeListIndex, 1);
    this.routeList = this.recalcWpIndices(this.routeList);

    if (entry.wp != null && entry.wp >= 0) {
      this.setSearchedRangeOrRoute(true);
      this.emitAll();
      this.paramsUpdate$.next();
    } else {
      this.ObservableRouteList.next([...this.routeList]);
    }
  }

  public deleteWaypointWithoutReplan(i: number): void {
    const routeListIdx = this.routeList.findIndex(el => el.wp === i);
    if (routeListIdx !== -1) {
      this.routeList.splice(routeListIdx, 1);
      this.routeList = this.recalcWpIndices(this.routeList);
      this.ObservableRouteList.next([...this.routeList]);
    }
    this.persistToStorage();
  }

  /** After navigation arrives at destination, reset to just the start waypoint. */
  public resetToFirstWaypoint(): void {
    if (this.routeList.length > 0) {
      this.routeList = [{ ...this.routeList[0], wp: 0 }];
      this.emitAll();
      this.ObservableNoRoute.next(true);
    }
  }

  /** Remove empty placeholder rows (called before navigation start). */
  public removeEmptyRouteListEntries(): void {
    const filtered = this.routeList.filter(el => el.lat !== 0 || el.lng !== 0);
    if (filtered.length !== this.routeList.length) {
      this.routeList = filtered;
      this.ObservableRouteList.next([...filtered]);
    }
  }

  public deleteChargingWaypoint(csid: string): void {
    this.routeList = this.recalcWpIndices(this.routeList.filter(el => !(el.csid && el.csid === csid)));
    this.emitAll();
    this.persistToStorage();
  }

  // ===== Mode =====

  private setSelectedDisplayModeInternal(mode: DisplayMode): void {
    const previous = this.selectedDisplayMode;
    this.selectedDisplayMode = mode;
    // Leaving planning drops the route: forget the addresses of the abandoned
    // waypoints so they can't be replayed onto the next plan.
    if (mode === DisplayMode.RANGE && previous !== DisplayMode.RANGE) {
      this.clearReverseGeocodedLocations();
    }
    this.ObservableSelectedDisplayMode.next(mode);
  }

  public setSelectedDisplayMode(mode: DisplayMode): void {
    this.setSelectedDisplayModeInternal(mode);
    // A mode switch usually ends in a recalculation, which persists on its own — but not always
    // (SHOW_ON_MAP does not), and the mode is only worth restoring if every deliberate change to
    // it is written down. The internal setter deliberately does not do this: it also runs from
    // the restore paths, which would have it write back what they had just read.
    this.persistToStorage();
  }

  public getSelectedDisplayMode(): DisplayMode {
    return this.selectedDisplayMode;
  }

  // ===== Reverse geocode state =====

  public setSearchedRangeOrRoute(value: boolean): void {
    this.searchedRangeOrRoute = value;
  }

  public getSearchedRangeOrRoute(): boolean {
    return this.searchedRangeOrRoute;
  }

  public setReverseGeocodedLocations(params: WaypointAddress[]): void {
    this.reverseGeocodedLocations = params;
    this.ObservableReverseGeocodedLocations.next(params);
  }

  public getReverseGeocodedLocations(): WaypointAddress[] | null {
    return this.reverseGeocodedLocations;
  }

  /**
   * ObservableReverseGeocodedLocations is a BehaviorSubject and PlanningPanelComponent
   * is created/destroyed with ROUTE mode, so a stale value would be replayed — and
   * written back over the route list — the next time planning opens.
   */
  public clearReverseGeocodedLocations(): void {
    this.reverseGeocodedLocations = null;
    this.ObservableReverseGeocodedLocations.next(null);
  }

  // ===== Params update trigger =====

  public triggerParamsUpdate(): void {
    this.paramsUpdate$.next();
  }

  // ===== Persistence =====

  public persistToStorage(): void {
    if (!this.settingsService.acceptCookies) return;
    // Strip csid from start waypoint in RANGE mode
    const toSave: RouteListElement[] = this.routeList
      .filter(el => el.lat !== 0 || el.lng !== 0)
      .map((el, i) => {
        if (i === 0 && this.selectedDisplayMode === DisplayMode.RANGE && el.csid) {
          const { csid, ...rest } = el;
          return rest;
        }
        return el;
      });
    localStorage.setItem('waypoints', JSON.stringify(toSave));
    // Stored beside the waypoints rather than inferred back from their count on restore, which
    // cannot tell a planned route being looked at in range mode from a plain two-stop route.
    localStorage.setItem('displayMode', this.selectedDisplayMode);
  }

  // ===== Helpers =====

  private emitAll(): void {
    this.ObservableRouteList.next([...this.routeList]);
    this.ObservableWaypointsParams.next(this.getWaypointsParams());
    const waypointCount = this.routeList.filter(el => el.wp != null && el.wp >= 0).length;
    this.ObservableNoRoute.next(waypointCount <= 1);
  }

  private recalcWpIndices(list: RouteListElement[]): RouteListElement[] {
    let wpIdx = 0;
    return list.map(el => {
      if (el.lat !== 0 || el.lng !== 0) {
        return { ...el, wp: wpIdx++ };
      }
      return { ...el, wp: -1 };
    });
  }

  private rebuildFromRouteList(): void {
    this.routeList = this.recalcWpIndices(this.routeList);
    this.setSearchedRangeOrRoute(true);
    this.emitAll();
    this.paramsUpdate$.next();
  }
}
